import { MdAutoFixHigh, MdRefresh, MdClose, MdOpenInFull, MdCloseFullscreen } from 'react-icons/md';
import type { SATheme } from '../../types/stylingAssistant.types';

interface SAHeaderProps {
  theme: SATheme;
  hasMessages: boolean;
  onRefresh: () => void;
  onClose: () => void;
  /** Expand popup → full page (only shown in popup mode) */
  onExpand?: () => void;
  /** Collapse full page → popup (only shown in full-page expanded mode) */
  onCollapse?: () => void;
  /** Which mode we're rendering in */
  variant?: 'popup' | 'page';
}

export default function SAHeader({
  theme,
  hasMessages,
  onRefresh,
  onClose,
  onExpand,
  onCollapse,
  variant = 'popup',
}: SAHeaderProps) {
  return (
    <div
      className="flex items-center justify-between px-6 py-3 flex-shrink-0"
      style={{ borderBottom: `1px solid ${theme.border}` }}
    >
      <div className="flex items-center gap-3">
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center"
          style={{
            background: 'linear-gradient(135deg, #EE4423 0%, #ff6b35 100%)',
          }}
        >
          <MdAutoFixHigh size={18} className="text-white" />
        </div>
        <div>
          <h2
            className="text-base font-semibold tracking-tight leading-tight"
            style={{ color: theme.text }}
          >
            Styling Assistant
          </h2>
          <p className="text-[10px] font-medium" style={{ color: theme.textMuted }}>
            AI-powered style recommendations
          </p>
        </div>
      </div>

      <div className="flex items-center gap-1">
        {/* Refresh / New conversation */}
        {hasMessages && (
          <button
            onClick={onRefresh}
            className="p-2 rounded-lg hover:bg-white/5 transition-all duration-200 cursor-pointer"
            style={{ color: theme.textSecondary }}
            aria-label="New conversation"
            title="New conversation"
          >
            <MdRefresh size={20} />
          </button>
        )}

        {/* Expand to full page (popup mode only) */}
        {variant === 'popup' && onExpand && (
          <button
            onClick={onExpand}
            className="p-2 rounded-lg hover:bg-white/5 transition-all duration-200 cursor-pointer group"
            style={{ color: theme.textSecondary }}
            aria-label="Open in full page"
            title="Open in full page"
          >
            <MdOpenInFull
              size={18}
              className="transition-transform duration-200 group-hover:scale-110"
            />
          </button>
        )}

        {/* Close (popup mode only — full page uses navigation) */}
        {variant === 'popup' && (
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/5 transition-all duration-200 cursor-pointer"
            style={{ color: theme.textSecondary }}
            aria-label="Close"
          >
            <MdClose size={20} />
          </button>
        )}
      </div>
    </div>
  );
}
